<!-- Form input combining a searchable text field with a filterable dropdown list of selectable items. -->

<script lang="ts"></script>

<script lang="ts" setup generic="T">
import { createFormField, formFieldEmits, formFieldProps } from '@nzyme/vue-forms/components/defineFormField.js';
import type { FormFieldController } from '@nzyme/vue-forms/components/defineFormField.js';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { defineProp } from '@nzyme/vue-utils/defineProp.js';
import type { Slots } from '@nzyme/vue-utils/slots.js';
import { Check, ChevronDown } from 'lucide-vue-next';
import type { AcceptableValue } from 'reka-ui';
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from 'reka-ui';
import { computed, ref } from 'vue';
import type { PropType } from 'vue';

import * as l from './ComboboxInput.loc.js';
import FormFieldWrapper from './FormFieldWrapper.vue';

const props = defineProps({
  ...formFieldProps<T>(),
  label: String,
  placeholder: String,
  items: { type: Array as PropType<T[]>, required: true },
  displayValue: defineProp<(item: T) => string>(),
  filterFunction: defineProp<(items: T[], searchTerm: string) => T[]>(),
});

defineEmits(formFieldEmits<T>());

defineSlots<
  Slots<{
    item: { item: T };
    info: { field: FormFieldController<T> };
  }>
>();

const t = useTranslate();
const formField = createFormField<T>({ props });
const formFieldUntyped = formField as FormFieldController<AcceptableValue>;
const searchTerm = ref('');

function getDisplayValue(item: T): string {
  if (item == null) {
    return '';
  }
  if (props.displayValue) {
    return props.displayValue(item);
  }

  return typeof item === 'string' ? item : JSON.stringify(item);
}

const filteredItems = computed(() => {
  const term = searchTerm.value;
  if (!term) {
    return props.items;
  }

  if (props.filterFunction) {
    return props.filterFunction(props.items, term);
  }

  const lowerTerm = term.toLowerCase();
  return props.items.filter(item => {
    return getDisplayValue(item).toLowerCase().includes(lowerTerm);
  });
});
</script>

<template>
  <FormFieldWrapper
    :field="formField"
    :label="label"
    :required="required"
    :disabled="disabled"
    :readonly="readonly"
  >
    <template #default="{ inputId }">
      <ComboboxRoot
        v-model="formFieldUntyped.value"
        :ignore-filter="true"
        :disabled="disabled || readonly"
        :class="css.root"
        open-on-click
      >
        <ComboboxAnchor :class="css.anchor">
          <ComboboxInput
            :id="inputId"
            v-model="searchTerm"
            :class="css.input"
            :display-value="getDisplayValue"
            :placeholder="placeholder"
            :disabled="disabled"
            :readonly="readonly"
            :aria-label="label"
            @focus="formField.inputAttrs.onFocus"
            @blur="formField.inputAttrs.onBlur"
          />
          <ComboboxTrigger
            :class="css.trigger"
            tabindex="-1"
          >
            <ChevronDown :class="css.triggerIcon" />
          </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxPortal>
          <ComboboxContent
            :class="css.content"
            position="popper"
            :side-offset="8"
          >
            <ComboboxViewport :class="css.viewport">
              <ComboboxEmpty :class="css.empty">
                {{ t(l.noResults) }}
              </ComboboxEmpty>
              <ComboboxItem
                v-for="(item, index) in filteredItems"
                :key="index"
                :value="item as AcceptableValue"
                :class="css.item"
              >
                <span :class="css.itemContent">
                  <slot
                    name="item"
                    :item="item"
                  >
                    {{ getDisplayValue(item) }}
                  </slot>
                </span>
                <Check :class="css.checkIcon" />
              </ComboboxItem>
            </ComboboxViewport>
          </ComboboxContent>
        </ComboboxPortal>
      </ComboboxRoot>
    </template>

    <template #info>
      <slot
        name="info"
        :field="formField"
      />
    </template>
  </FormFieldWrapper>
</template>

<style lang="scss" module="css">
.root {
  flex: 1;
  min-width: 0;
  display: flex;
}

.anchor {
  flex: 1;
  min-width: 0;
  display: flex;
}

.input {
  flex: 1;
  min-width: 0;
}

.trigger {
  background: none;
  border: none;
  cursor: pointer;
  padding: $spacing-04 $spacing-12 $spacing-04 $spacing-04;
  display: flex;
  align-items: center;
  color: $color-gray-600;

  &:hover {
    color: $color-gray-800;
  }
}

.triggerIcon {
  width: 20px;
  height: 20px;
}

.content {
  background: $color-white;
  border: 1px solid $color-gray-200;
  border-radius: $border-radius-08;
  box-shadow: soft-shadow(50px, 0.2);
  overflow: hidden;
  z-index: 100;
  width: var(--reka-combobox-trigger-width);

  &[data-state='open'] {
    animation: combobox-show 0.2s ease-out;
  }

  &[data-state='closed'] {
    animation: combobox-hide 0.2s ease-out;
  }
}

.viewport {
  max-height: 240px;
  overflow-y: auto;
  padding: $spacing-04;
}

.empty {
  padding: $spacing-12 $spacing-16;
  color: $color-gray-600;
  font-size: $font-size-14;
  text-align: center;
}

.item {
  display: flex;
  align-items: center;
  gap: $spacing-08;
  padding: $spacing-08 $spacing-12;
  border-radius: $border-radius-06;
  cursor: pointer;
  outline: none;
  font-size: $font-size-14;
  transition: background-color 0.15s ease-out;
  user-select: none;

  &[data-highlighted] {
    background: $color-gray-50;
  }

  &[data-state='checked'] {
    color: $color-blue-600;
  }

  &[data-disabled] {
    color: $color-gray-600;
    cursor: not-allowed;
  }
}

.itemContent {
  flex: 1;
  min-width: 0;
}

.checkIcon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  visibility: hidden;

  [data-state='checked'] & {
    visibility: visible;
  }
}

@keyframes combobox-show {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes combobox-hide {
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0.95);
  }
}
</style>
